import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Badge } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { FaFolder, FaFile, FaDownload, FaTrash, FaUpload, FaClock } from 'react-icons/fa';
import { formatDateTimeIST } from '../utils/dateFormatter';
import Swal from 'sweetalert2';
import { fileAPI } from '../services/api';

const Files = () => {
  const { isAdmin, isHR, user } = useAuth();
  const [files, setFiles] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await fileAPI.getAll();
      setFiles(response.data);
    } catch (error) {
      console.error('Error fetching files:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error loading files',
        confirmButtonColor: '#635bff'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;

    setUploading(true);
    try {
      await fileAPI.upload(selectedFile, description);

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'File uploaded successfully!',
        confirmButtonColor: '#635bff'
      });

      setShowUploadModal(false);
      setSelectedFile(null);
      setDescription('');

      // Refresh files list
      fetchFiles();
    } catch (error) {
      console.error('Error uploading file:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || 'Error uploading file',
        confirmButtonColor: '#635bff'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    const result = await Swal.fire({
      title: 'Delete File?',
      text: "Are you sure you want to delete this file?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#635bff',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await fileAPI.delete(fileId);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'File deleted successfully',
          confirmButtonColor: '#635bff'
        });

        // Refresh files list
        fetchFiles();
      } catch (error) {
        console.error('Error deleting file:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.detail || 'Error deleting file',
          confirmButtonColor: '#635bff'
        });
      }
    }
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      const response = await fileAPI.getDownloadUrl(fileId);
      const downloadUrl = response.data.download_url;

      // Open download URL in new tab
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Error downloading file:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || 'Error downloading file',
        confirmButtonColor: '#635bff'
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    const iconMap = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      xls: '📊',
      xlsx: '📊',
      ppt: '📊',
      pptx: '📊',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️',
      gif: '🖼️',
      zip: '🗜️',
      rar: '🗜️',
      txt: '📃',
    };
    return iconMap[extension] || '📄';
  };

  return (
    <>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#0a0a0a',
          letterSpacing: '-0.02em',
          margin: 0
        }}>
          Files
        </h1>
        {(isAdmin || isHR) && (
          <Button
            onClick={() => setShowUploadModal(true)}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              backgroundColor: '#635bff',
              color: 'white',
              border: 'none',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaUpload size={14} />
            Upload File
          </Button>
        )}
      </div>

      <Card style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
      }}>
        <Card.Header style={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px',
          borderRadius: '12px 12px 0 0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaFolder size={18} style={{ color: '#635bff' }} />
            <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#0a0a0a' }}>
              Shared Files
            </h5>
          </div>
        </Card.Header>
        <Card.Body style={{ padding: 0 }}>
          {files.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#9ca3af'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📁</div>
              <div style={{ fontSize: '16px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                No files yet
              </div>
              <div style={{ fontSize: '14px' }}>
                Upload files to share with your team
              </div>
            </div>
          ) : (
            <Table hover responsive style={{ margin: 0 }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr>
                  <th style={{ padding: '12px 24px', fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>File</th>
                  <th style={{ padding: '12px 24px', fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Description</th>
                  <th style={{ padding: '12px 24px', fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Size</th>
                  <th style={{ padding: '12px 24px', fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Uploaded</th>
                  <th style={{ padding: '12px 24px', fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file) => (
                  <tr key={file.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '16px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '24px' }}>{getFileIcon(file.original_name)}</span>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '500', color: '#0a0a0a' }}>
                            {file.original_name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            by {file.uploaded_by_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px 24px', fontSize: '14px', color: '#374151' }}>
                      {file.description || '-'}
                    </td>
                    <td style={{ padding: '16px 24px', fontSize: '14px', color: '#374151' }}>
                      {formatFileSize(file.file_size)}
                    </td>
                    <td style={{ padding: '16px 24px', fontSize: '13px', color: '#6b7280' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FaClock size={12} />
                        {formatDateTimeIST(file.created_at)}
                      </div>
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          size="sm"
                          onClick={() => handleDownload(file.id, file.original_name)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            backgroundColor: '#635bff',
                            color: 'white',
                            border: 'none',
                            fontSize: '13px',
                            cursor: 'pointer'
                          }}
                        >
                          <FaDownload size={12} />
                        </Button>
                        {(isAdmin || isHR) && (
                          <Button
                            size="sm"
                            onClick={() => handleDelete(file.id)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              backgroundColor: 'white',
                              color: '#ef4444',
                              border: '1px solid #e5e7eb',
                              fontSize: '13px',
                              cursor: 'pointer'
                            }}
                          >
                            <FaTrash size={12} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Upload Modal */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)}>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb' }}>
          <Modal.Title style={{ fontSize: '20px', fontWeight: '600', color: '#0a0a0a', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FaUpload style={{ color: '#635bff' }} />
            Upload File
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpload}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Select File
              </Form.Label>
              <Form.Control
                type="file"
                onChange={handleFileSelect}
                required
                style={{
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  padding: '10px 12px',
                  fontSize: '14px'
                }}
              />
              {selectedFile && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>
                  Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </div>
              )}
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Description (Optional)
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  padding: '10px 12px',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}
                placeholder="Add a description for this file..."
              />
            </Form.Group>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button
                type="button"
                onClick={() => setShowUploadModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={uploading || !selectedFile}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  backgroundColor: '#635bff',
                  color: 'white',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: uploading || !selectedFile ? 0.5 : 1,
                  cursor: uploading || !selectedFile ? 'not-allowed' : 'pointer'
                }}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Files;
